import axios from 'axios'
import qs from 'qs'
import { Message, MessageBox } from 'element-ui'
import { getToken, removeToken } from '@/utils/auth'
import { resetRouter } from '@/router'

// AI训战后端（duyisheng-ai-train-server，独立部署，见 REQ-2026-117533 实现记录）专用 axios 实例。
// baseURL 完全依赖环境变量 VUE_APP_AI_TRAIN_API，不设默认值——没配就应该显式报错，
// 不能悄悄打到别的后端去（历史上 VUE_APP_BASE_API 就是这么定的，这里照抄）。
const aiTrainService = axios.create({
  baseURL: process.env.VUE_APP_AI_TRAIN_API,
  timeout: 500000,
  paramsSerializer(params) {
    return qs.stringify(params, {
      arrayFormat: 'repeat'
    })
  }
})

// 请求拦截器：携带管理端 token。
// AI训战服务器按 header 值的前缀区分管理端/学员端（见实现记录"更正一"），
// 管理端固定 `Token: DU <token>`，与主 admin-api 的 sa-token 前缀一致，
// 因为登录态本来就是同一个 sys_user + 同一个 Redis db6 会话。
aiTrainService.interceptors.request.use(
  config => {
    if (Object.prototype.toString.call(config.data) === '[object Object]' && config.isFormData) {
      const list = []
      const obj = config.data
      for (const key in obj) {
        if (obj[key] !== undefined && obj[key] !== null) {
          list.push(`${key}=${encodeURIComponent(obj[key])}`)
        }
      }
      config.data = list.join('&')
    }

    if (getToken()) {
      config.headers['Token'] = 'DU ' + getToken()
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// AI 服务普通接口统一 ApiResult { code, msg, data }，仅 code=0 成功。
// 下载/显式 skipInterceptor 保留原始 response，由调用方处理对应协议。
aiTrainService.interceptors.response.use(
  response => {
    if (response.config.skipInterceptor || response.config.responseType === 'blob') {
      return response
    }

    const data = response.data
    if (response.status >= 200 && response.status < 300) {
      if (data && (data.errorCode === 'UNAUTHORIZED_LOGIN' ||
          (data.success === false && data.errorCode && String(data.errorCode).includes('UNAUTHORIZED')))) {
        const errorMsg = data.errorMessage || data.msg || data.message || '用户会话数据异常，请重新登录！'
        MessageBox.alert(errorMsg, '错误', {
          confirmButtonText: '确定',
          type: 'error'
        }).then(() => {
          removeToken()
          resetRouter()
          location.reload()
        })
        return Promise.reject('error')
      }

      if (!data || (data.code !== 0 && data.code !== '0') || data.success === false) {
        const message = (data && (data.msg || data.message)) || 'AI训战返回数据格式异常或操作失败，请重试'
        if (!response.config.silent) {
          Message({ message, type: 'error' })
        }
        const error = new Error(message)
        error.code = data && data.code
        return Promise.reject(error)
      }

      return data
    }
    return Promise.reject(new Error(data))
  },
  error => {
    let message = error.message || '网络请求失败'

    if (error.response) {
      const { status, data } = error.response
      if (data && (data.msg || data.message)) {
        message = data.msg || data.message
      } else if (status === 401) {
        message = '登录状态失效，请重新登录'
      } else if (status === 403) {
        message = '没有权限访问'
      } else if (status === 404) {
        message = '请求的内容不存在或已移除，请刷新后重试'
      } else if (status === 500) {
        message = '服务器内部错误'
      } else {
        message = `请求失败 (${status})`
      }
    } else if (error.request) {
      message = 'AI训战服务连接失败，请稍后重试或联系管理员'
    }

    if (!error.config || !error.config.silent) {
      Message({ message, type: 'error', duration: 5 * 1000 })
    }
    return Promise.reject(error)
  }
)

export default aiTrainService
